<template>
  <div id="about">
    <HeroSlider
      v-if="aboutUsHeroSliderContent"
      :slider-data="aboutUsHeroSliderContent"
    />
    <LazyHydrate
      when-visible
      v-if="aboutUsDescirptionContent && aboutUsDescirptionContent.active"
    >
      <Description
        :description-text="aboutUsDescirptionContent.data.description"
      />
    </LazyHydrate>
    <template v-if="aboutUsBannerImgContent && aboutUsBannerImgContent.active">
      <div class="about-wide-banner">
        <WideBanner
          :data="aboutUsBannerImgContent.data"
          name="aboutUsMidVideo"
          key="aboutUsMidVideo"
        />
        <HeroSlideContent
          v-if="aboutHeroSliderContent && aboutHeroSliderContent.active"
          :slide-data="aboutHeroSliderContent"
        />
      </div>
    </template>
    <LazyHydrate
      when-visible
      v-if="
        aboutUsTextImgBlockOneContent && aboutUsTextImgBlockOneContent.active
      "
    >
      <ImageWithText :data="aboutUsTextImgBlockOneContent" />
    </LazyHydrate>
    <LazyHydrate
      when-visible
      v-if="
        aboutUsNoCtaHandcraftedContent && aboutUsNoCtaHandcraftedContent.active
      "
    >
      <HandCraftedBlock
        :handcrafted-data="aboutUsNoCtaHandcraftedContent.data"
      />
    </LazyHydrate>
    <LazyHydrate
      when-visible
      v-if="
        aboutUsTextImgBlockTwoContent && aboutUsTextImgBlockTwoContent.active
      "
    >
      <ImageWithText :data="aboutUsTextImgBlockTwoContent" />
    </LazyHydrate>
    <LazyHydrate
      when-visible
      v-if="
        aboutUsWithCtaHandcraftedContent &&
        aboutUsWithCtaHandcraftedContent.active
      "
    >
      <HandCraftedBlock
        :handcrafted-data="aboutUsWithCtaHandcraftedContent.data"
        :key="Date.now()"
        :showModalButton="true"
      />
    </LazyHydrate>
    <MeetTheGlassMasters />
  </div>
</template>

<script type="module">
import {
  defineComponent,
  ref,
  computed,
  useContext,
  useFetch,
} from '@nuxtjs/composition-api';
import { useCache, CacheTagPrefix } from '@gemini/vsf-cache';
import { useContent, contentGetters } from '@gemini-vsf/composables';
import Description from '~/components/Editorial/About/Description.vue';
import HandCraftedBlock from '~/components/Home/HandCraftedBlock.vue';
import HeroSlider from '~/components/Homepage/HeroSlider/HeroSlider.vue';
import ImageWithText from '~/components/General/ImageWithText.vue';
import LazyHydrate from 'vue-lazy-hydration';
import MeetTheGlassMasters from '~/components/Editorial/About/MeetTheGlassMasters.vue';
import WideBanner from '~/components/Editorial/Commons/WideBanner.vue';
import HeroSlideContent from '~/components/Homepage/HeroSlider/HeroSlideContent.vue';
import { handleSeo } from '~/helpers/seo/handleSeo';
export default defineComponent({
  name: 'AboutPage',
  components: {
    Description,
    HandCraftedBlock,
    HeroSlider,
    ImageWithText,
    LazyHydrate,
    MeetTheGlassMasters,
    WideBanner,
    HeroSlideContent,
  },
  setup() {
    const { addTags } = useCache();
    const {
      app: {
        i18n: {
          localeProperties: { localeValue },
        },
      },
    } = useContext();
    const { app } = useContext();
    const { blocks, loadBlocks } = useContent(`about-${localeValue}`);
    const aboutHeroSliderContent = ref();
    const linkLabel = 'link-label';
    const linkUrl = 'link-url';

    const setSlideContent = (content) => {
      if (content?.active) {
        aboutHeroSliderContent.value = {
          active: content.active,
          title: content.data.title,
          ...(content.data.cta
            ? {
                cta: {
                  text: content.data.cta?.[linkLabel],
                  subtext: content.data.cta?.subtext,
                  href: content.data.cta?.[linkUrl],
                },
              }
            : {}),
        };
      }
    };

    const aboutUsDescirptionSelector = `about-description-${localeValue}`;
    const aboutUsDescirptionContent = ref();

    const aboutUsHeroSliderSelector = `about-us-hero-slide-${localeValue}`;
    const aboutUsHeroSliderContent = ref();

    const aboutUsTextImgBlockSelector = `text-img-${localeValue}`;
    const aboutUsTextImgBlockOneContent = ref();
    const aboutUsTextImgBlockTwoContent = ref();

    const aboutUsBannerImgSelector = `about-us-image-banner-${localeValue}`;
    const aboutUsBannerImgContent = ref();

    const aboutUsNoCtaHandcraftedSelector = `about-us-no-cta-handcrafted-${localeValue}`;
    const aboutUsNoCtaHandcraftedContent = ref();

    const aboutUsWithCtaHandcraftedSelector = `about-us-handcrafted-${localeValue}`;
    const aboutUsWithCtaHandcraftedContent = ref();

    useFetch(async () => {
      await loadBlocks({
        identifiers: [
          aboutUsDescirptionSelector,
          aboutUsHeroSliderSelector,
          aboutUsTextImgBlockSelector,
          aboutUsBannerImgSelector,
          aboutUsNoCtaHandcraftedSelector,
          aboutUsWithCtaHandcraftedSelector,
        ],
      });
      const blockData = await contentGetters.getCmsBlockContent(blocks.value);
      aboutUsDescirptionContent.value = blockData[aboutUsDescirptionSelector];
      const aboutUsHeroSliderBlockData = blockData[aboutUsHeroSliderSelector];
      if (aboutUsHeroSliderBlockData && aboutUsHeroSliderBlockData.active) {
        aboutUsHeroSliderContent.value = aboutUsHeroSliderBlockData.data;
        aboutUsHeroSliderContent.value.active = true;
      }
      aboutUsTextImgBlockOneContent.value =
        blockData[aboutUsTextImgBlockSelector].data[0];
      aboutUsTextImgBlockTwoContent.value =
        blockData[aboutUsTextImgBlockSelector].data[1];
      aboutUsBannerImgContent.value = blockData[aboutUsBannerImgSelector];
      setSlideContent(aboutUsBannerImgContent.value);
      aboutUsNoCtaHandcraftedContent.value =
        blockData[aboutUsNoCtaHandcraftedSelector];
      aboutUsWithCtaHandcraftedContent.value =
        blockData[aboutUsWithCtaHandcraftedSelector];
      addTags([
        { prefix: CacheTagPrefix.View, value: 'about' },
        { prefix: CacheTagPrefix.View, value: 'page' },
      ]);
    });

    const metaTitle = computed(() =>
      app.i18n.t('About Us - Sogni Di Cristallo®')
    );
    const metaDescription = computed(() => 'About Us');
    const { getCanonical } = handleSeo();
    const canonicalLink = computed(() => getCanonical());

    return {
      localePath: app.localePath,
      metaTitle,
      metaDescription,
      canonicalLink,
      aboutUsDescirptionContent,
      aboutUsHeroSliderContent,
      aboutUsTextImgBlockOneContent,
      aboutUsTextImgBlockTwoContent,
      aboutUsBannerImgContent,
      aboutUsNoCtaHandcraftedContent,
      aboutUsWithCtaHandcraftedContent,
      aboutHeroSliderContent,
      setSlideContent,
    };
  },
  head() {
    return {
      title: this?.metaTitle,
      meta: [
        { hid: 'robots', name: 'robots', content: 'index, follow' },
        {
          hid: 'og:description',
          property: 'og:description',
          content: this?.metaDescription,
        },
        {
          hid: 'description',
          name: 'description',
          content: this?.metaDescription,
        },
        //og:url
        {
          hid: 'og:url',
          property: 'og:url',
          content:
            'https://www.sognidicristallo.com' + this?.localePath('/about'),
        },
        { hid: 'og:title', property: 'og:title', content: this?.metaTitle },
        { hid: 'fb:app_id', property: 'fb:app_id', content: '208478719244524' },
      ],
      link: [{ ...this.canonicalLink }],
    };
  },
});
</script>
<style lang="scss">
.about-wide-banner {
  position: relative;

  .hero-slider__slide__content {
    top: 31.21%;
    left: unset;
    transform: unset;
    @include from-desktop-min {
      top: 44.46%;
    }
  }
}
</style>
